import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Radio,
  RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import BoutonAide from "../../components/charte/BoutonAide";
import { BoutonPrincipal, BoutonSecondaire } from "../../components/charte/Boutons";
import { AIDE_FACTURATION } from "../../aide/textesAide";
import { STYLE_TON } from "../../theme/tribeTheme";
import { useDroitPaiement } from "../../utilities/droitPaiement";
import { messageServeur } from "../../utilities/userService";
import {
  DocumentEmis, EtatMission, LIBELLE_NATURE, LIBELLE_TYPE, SuiviFacture, VueClient, clientDeLaMission,
  documentsDeLaMission, emettreDocument, etatMission, jour, listerClients, nouvelleCle, rattacherClient,
  suiviFacture, telechargerPdf, xof,
} from "../../utilities/facturationService";
import {
  DialogAnnulation, DialogAvoir, DialogClient, DialogEncaissement, EditeurLignes, Erreur, Libelle, LigneSaisie,
  PastilleEtat, lignesPourServeur,
} from "./composantsFacturation";

/* Bloc Facturation de la fiche de quête : client rattaché, état de la mission
   (contrat, facturé, reste à facturer, encaissé, reste dû), émission des
   documents, suivi de chaque facture. Tout vient d'admin-service ; les PDF
   sont ceux que le serveur a produits et archivés. */
export default function BlocFacturation({ questId, titre, budgetClient, onChange, actionsEnTete }: {
  questId: string; titre: string; budgetClient?: number | null; onChange: () => void; actionsEnTete?: React.ReactNode;
}) {
  const droitPaiement = useDroitPaiement();
  const [client, setClient] = useState<VueClient | null>(null);
  const [etat, setEtat] = useState<EtatMission | null>(null);
  const [docs, setDocs] = useState<DocumentEmis[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ficheOuverte, setFicheOuverte] = useState<VueClient | null | "nouveau">(null);
  const [choix, setChoix] = useState(false);
  const [emission, setEmission] = useState<"DEVIS" | "CONTRAT" | "FACTURE" | null>(null);
  const [ouverte, setOuverte] = useState<number | null>(null);
  const [avoirSur, setAvoirSur] = useState<DocumentEmis | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [c, e, d] = await Promise.all([clientDeLaMission(questId), etatMission(questId), documentsDeLaMission(questId)]);
      setClient(c); setEtat(e); setDocs(d);
    } catch (e: any) {
      setErreur(messageServeur(e, "Facturation illisible : admin-service ne répond pas."));
    }
  }, [questId]);
  useEffect(() => { charger(); }, [charger]);
  const apres = () => { charger(); onChange(); };

  const encaisse = (etat?.factures ?? []).reduce((s, f) => s + (f.montantEncaisse ?? 0), 0);
  const chiffres: [string, number | null | undefined][] = [
    ["Contrat", etat?.montantContratTtc], ["Facturé", etat?.montantFactureTtc], ["Reste à facturer", etat?.resteAFacturerTtc],
    ["Encaissé", encaisse], ["Reste dû", etat?.resteDuTtc],
  ];

  return (
    <Box>
      {erreur && <Erreur message={erreur} />}

      {/* Client rattaché */}
      <Libelle aide={AIDE_FACTURATION.rattachement}>Client facturé</Libelle>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        {client ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{client.client.raisonSociale}</Typography>
            {client.complet
              ? <Typography sx={{ fontSize: 12.5, color: STYLE_TON.succes.color }}>complet</Typography>
              : <Typography sx={{ fontSize: 12.5, color: STYLE_TON.erreur.color }}>manque : {client.manquants.join(", ")}</Typography>}
            <BoutonSecondaire size="small" onClick={() => setFicheOuverte(client)}>Modifier la fiche</BoutonSecondaire>
            {docs.length === 0 && <BoutonSecondaire size="small" onClick={() => setChoix(true)}>Changer de client</BoutonSecondaire>}
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 13, color: STYLE_TON.attente.color }}>Aucun client rattaché : aucun document ne peut être émis.</Typography>
            <BoutonSecondaire size="small" onClick={() => setChoix(true)}>Choisir un client</BoutonSecondaire>
            <BoutonSecondaire size="small" onClick={() => setFicheOuverte("nouveau")}>Nouveau client</BoutonSecondaire>
          </>
        )}
      </Box>

      {/* État de la mission */}
      <Libelle aide={AIDE_FACTURATION.etatMission}>État de la facturation</Libelle>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1, mb: 2 }}>
        {chiffres.map(([l, v]) => (
          <Box key={l} sx={{ border: "1px solid var(--c-border)", borderRadius: "6px", px: 1.25, py: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: "var(--c-text-secondary)" }}>{l}</Typography>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>{xof(v)}</Typography>
          </Box>
        ))}
      </Box>

      {/* Émission */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <BoutonSecondaire size="small" disabled={!client?.complet} onClick={() => setEmission("DEVIS")}>Émettre un devis</BoutonSecondaire>
        <BoutonSecondaire size="small" disabled={!client?.complet} onClick={() => setEmission("CONTRAT")}>Émettre un contrat</BoutonSecondaire>
        <BoutonPrincipal size="small" disabled={!client?.complet || !etat?.engagement || (etat?.resteAFacturerTtc ?? 0) <= 0}
          onClick={() => setEmission("FACTURE")}>Émettre une facture</BoutonPrincipal>
        {actionsEnTete}
      </Box>

      {/* Documents */}
      <Libelle aide={AIDE_FACTURATION.documents}>Documents émis</Libelle>
      {docs.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "var(--c-text-secondary)" }}>Aucun document émis pour cette quête.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>{["Document", "Émis le", "Montant TTC", "Paiement", "Reste dû", ""].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {docs.map((d) => (
              <React.Fragment key={d.id}>
                <TableRow>
                  <TableCell>
                    <b>{d.numero}</b> · {LIBELLE_TYPE[d.type]}{d.natureFacture ? ` (${LIBELLE_NATURE[d.natureFacture] ?? d.natureFacture})` : ""}
                    {d.documentOrigineNumero ? ` sur ${d.documentOrigineNumero}` : ""}
                  </TableCell>
                  <TableCell>{jour(d.emisLe)}</TableCell>
                  <TableCell>{xof(d.montantTtc)}</TableCell>
                  <TableCell><PastilleEtat etat={d.etatPaiement} /></TableCell>
                  <TableCell>{d.type === "FACTURE" ? xof(d.resteDuTtc) : ""}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <BoutonSecondaire size="small" sx={{ mr: 1, py: 0.2 }}
                      onClick={() => telechargerPdf(d.id, d.numero).catch((e) => setErreur(messageServeur(e, "PDF indisponible.")))}>PDF</BoutonSecondaire>
                    {d.type === "FACTURE" && (
                      <>
                        <BoutonSecondaire size="small" sx={{ mr: 1, py: 0.2 }} onClick={() => setOuverte(ouverte === d.id ? null : d.id)}>
                          {ouverte === d.id ? "Fermer" : "Suivi"}
                        </BoutonSecondaire>
                        <BoutonSecondaire size="small" sx={{ py: 0.2 }} onClick={() => setAvoirSur(d)}>Avoir</BoutonSecondaire>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                {d.type === "FACTURE" && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: ouverte === d.id ? undefined : "none" }}>
                      <Collapse in={ouverte === d.id} unmountOnExit>
                        <SuiviDeFacture id={d.id} droitPaiement={droitPaiement} onChange={apres} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      <DialogClient ouvert={ficheOuverte !== null} vue={ficheOuverte === "nouveau" ? null : ficheOuverte}
        onFerme={() => setFicheOuverte(null)}
        onEnregistre={async (v) => {
          setFicheOuverte(null);
          if (!client && v.client.id) {
            try { await rattacherClient(questId, v.client.id); } catch (e: any) { setErreur(messageServeur(e, "Rattachement refusé.")); }
          }
          apres();
        }} />
      <DialogChoixClient ouvert={choix} onFerme={() => setChoix(false)}
        onChoisi={async (id) => {
          setChoix(false);
          try { await rattacherClient(questId, id); } catch (e: any) { setErreur(messageServeur(e, "Rattachement refusé.")); }
          apres();
        }} />
      <DialogEmission type={emission} questId={questId} titre={titre} budgetClient={budgetClient} etat={etat}
        onFerme={() => setEmission(null)} onEmis={() => { setEmission(null); apres(); }} />
      <DialogAvoir facture={avoirSur} onFerme={() => setAvoirSur(null)} onEmis={() => { setAvoirSur(null); apres(); }} />
    </Box>
  );
}

/* ── Choix d'un client existant ── */

function DialogChoixClient({ ouvert, onFerme, onChoisi }: { ouvert: boolean; onFerme: () => void; onChoisi: (id: number) => void }) {
  const [clients, setClients] = useState<VueClient[]>([]);
  const [id, setId] = useState<number | "">("");
  useEffect(() => { if (ouvert) { setId(""); listerClients().then(setClients).catch(() => setClients([])); } }, [ouvert]);
  return (
    <Dialog open={ouvert} onClose={onFerme} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>Client facturé de la quête <BoutonAide texte={AIDE_FACTURATION.rattachement} /></DialogTitle>
      <DialogContent>
        <TextField select fullWidth size="small" label="Client" value={id} sx={{ mt: 1 }} onChange={(e) => setId(Number(e.target.value))}>
          {clients.map((v) => (
            <MenuItem key={v.client.id} value={v.client.id}>
              {v.client.raisonSociale} {v.complet ? "" : `(manque : ${v.manquants.join(", ")})`}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Annuler</BoutonSecondaire>
        <BoutonPrincipal disabled={id === ""} onClick={() => id !== "" && onChoisi(id)}>Rattacher</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

/* ── Émission : devis ou contrat avec lignes, facture en totalité, solde ou acompte ── */

function DialogEmission({ type, questId, titre, budgetClient, etat, onFerme, onEmis }: {
  type: "DEVIS" | "CONTRAT" | "FACTURE" | null; questId: string; titre: string; budgetClient?: number | null;
  etat: EtatMission | null; onFerme: () => void; onEmis: () => void;
}) {
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const [mode, setMode] = useState<"reste" | "pourcentage" | "montant">("reste");
  const [valeur, setValeur] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [cle, setCle] = useState("");
  useEffect(() => {
    if (!type) return;
    setErreur(null); setMode("reste"); setValeur(""); setCle(nouvelleCle(type.toLowerCase()));
    setLignes([{ libelle: `Mission de collecte : ${titre}`, quantite: "1",
      prixUnitaireHt: budgetClient ? String(Math.round(budgetClient)) : "", tauxTva: "18" }]);
  }, [type, titre, budgetClient]);
  if (!type) return null;
  const dejaFacture = (etat?.montantFactureTtc ?? 0) > 0;
  const emettre = async () => {
    setEnvoi(true); setErreur(null);
    try {
      if (type === "FACTURE") {
        const n = parseFloat(valeur.replace(",", ".").replace(/\s/g, ""));
        await emettreDocument({ questId, type, cle,
          acompte: mode === "pourcentage" ? { pourcentage: n } : mode === "montant" ? { montantTtc: Math.round(n) } : undefined });
      } else {
        await emettreDocument({ questId, type, cle, lignes: lignesPourServeur(lignes) as any });
      }
      onEmis();
    } catch (e: any) {
      setErreur(messageServeur(e, "Émission refusée."));
    } finally { setEnvoi(false); }
  };
  const nom = type === "DEVIS" ? "un devis" : type === "CONTRAT" ? "un contrat" : "une facture";
  return (
    <Dialog open onClose={onFerme} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Émettre {nom} <BoutonAide texte={type === "FACTURE" ? AIDE_FACTURATION.facture : AIDE_FACTURATION.devis} />
      </DialogTitle>
      <DialogContent>
        {type === "FACTURE" ? (
          <>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              Contrat : <b>{etat?.engagement?.numero}</b> ({xof(etat?.montantContratTtc)} TTC) · déjà facturé : {xof(etat?.montantFactureTtc)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Reste à facturer : {xof(etat?.resteAFacturerTtc)}</Typography>
              <BoutonAide texte={AIDE_FACTURATION.resteAFacturer} />
            </Box>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as any)}>
              <FormControlLabel value="reste" control={<Radio />}
                label={dejaFacture ? "Solde : tout le reste à facturer, factures antérieures déduites" : "Totalité du contrat"} />
              <FormControlLabel value="pourcentage" control={<Radio />} label="Acompte en pourcentage du contrat" />
              <FormControlLabel value="montant" control={<Radio />} label="Acompte en montant TTC" />
            </RadioGroup>
            {mode !== "reste" && (
              <TextField size="small" sx={{ mt: 1, width: 260 }} value={valeur} onChange={(e) => setValeur(e.target.value)}
                label={mode === "pourcentage" ? "Pourcentage (ex. 40)" : "Montant TTC (XOF)"} />
            )}
          </>
        ) : (
          <EditeurLignes lignes={lignes} onChange={setLignes} />
        )}
        <Erreur message={erreur} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Annuler</BoutonSecondaire>
        <BoutonPrincipal disabled={envoi || (type === "FACTURE" && mode !== "reste" && !valeur.trim())} onClick={emettre}>Émettre</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

/* ── Suivi d'une facture : reste dû, état, avoirs, encaissements ── */

export function SuiviDeFacture({ id, droitPaiement, onChange }: { id: number; droitPaiement: boolean; onChange: () => void }) {
  const [s, setS] = useState<SuiviFacture | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [encaisser, setEncaisser] = useState(false);
  const [aAnnuler, setAAnnuler] = useState<{ id: number; montant: number; factureNumero: string } | null>(null);
  const charger = useCallback(() => {
    suiviFacture(id).then(setS).catch((e) => setErreur(messageServeur(e, "Suivi illisible.")));
  }, [id]);
  useEffect(() => { charger(); }, [charger]);
  if (erreur) return <Box sx={{ p: 2 }}><Erreur message={erreur} /></Box>;
  if (!s) return <Typography sx={{ p: 2, fontSize: 13 }}>Lecture…</Typography>;
  const d = s.document;
  const annules = new Set((s.encaissements ?? []).map((e) => e.annuleEncaissementId).filter(Boolean));
  const apres = () => { charger(); onChange(); };
  return (
    <Box sx={{ p: 2, background: "var(--c-surface-2)" }}>
      <Libelle aide={AIDE_FACTURATION.suiviFacture}>Suivi de {d.numero}</Libelle>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontSize: 13 }}>TTC : <b>{xof(d.montantTtc)}</b></Typography>
        <Typography sx={{ fontSize: 13 }}>Avoirs : <b>{xof(s.montantAvoirsTtc)}</b></Typography>
        <Typography sx={{ fontSize: 13 }}>Encaissé : <b>{xof(s.montantEncaisse)}</b></Typography>
        <Typography sx={{ fontSize: 13 }}>Reste dû : <b>{xof(s.resteTtc)}</b></Typography>
        <PastilleEtat etat={s.etatPaiement} />
        {d.dateEcheance && <Typography sx={{ fontSize: 13 }}>Échéance : {jour(d.dateEcheance)}</Typography>}
        {d.natureFacture && <Typography sx={{ fontSize: 13 }}>{LIBELLE_NATURE[d.natureFacture] ?? d.natureFacture}</Typography>}
      </Box>
      {(s.avoirs ?? []).length > 0 && (
        <Typography sx={{ fontSize: 13, mb: 1 }}>
          Avoirs : {(s.avoirs ?? []).map((a) => `${a.numero} (${xof(a.montantTtc)}, ${jour(a.emisLe)})`).join(" · ")}
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Encaissements</Typography>
      {(s.encaissements ?? []).length === 0
        ? <Typography sx={{ fontSize: 13, color: "var(--c-text-secondary)", mb: 1 }}>Aucun encaissement.</Typography>
        : (
          <Table size="small" sx={{ mb: 1 }}>
            <TableBody>
              {(s.encaissements ?? []).map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{jour(e.dateValeur)}</TableCell>
                  <TableCell>{xof(e.montant)}</TableCell>
                  <TableCell>{e.mode}{e.referenceExterne ? ` · ${e.referenceExterne}` : ""}</TableCell>
                  <TableCell>{e.annuleEncaissementId ? `Annule #${e.annuleEncaissementId} : ${e.motifAnnulation}` : e.tropPercu ? "Trop-perçu" : e.note ?? ""}</TableCell>
                  <TableCell>{e.saisiPar}</TableCell>
                  <TableCell align="right">
                    {droitPaiement && !e.annuleEncaissementId && !annules.has(e.id) && (
                      <BoutonSecondaire size="small" sx={{ py: 0.2 }} onClick={() => setAAnnuler(e)}>Annuler</BoutonSecondaire>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      {droitPaiement
        ? <BoutonPrincipal size="small" onClick={() => setEncaisser(true)}>Saisir un encaissement</BoutonPrincipal>
        : <Typography sx={{ fontSize: 12.5, color: "var(--c-text-secondary)" }}>Saisie réservée aux admins qui ont le droit de paiement.</Typography>}
      <DialogEncaissement ouvert={encaisser} facture={{ id: d.id, numero: d.numero, resteDuTtc: s.resteTtc }}
        onFerme={() => setEncaisser(false)} onEnregistre={() => { setEncaisser(false); apres(); }} />
      <DialogAnnulation encaissement={aAnnuler} onFerme={() => setAAnnuler(null)} onEnregistre={() => { setAAnnuler(null); apres(); }} />
    </Box>
  );
}
